import { useState, useEffect, useRef, useCallback } from 'react';
import type { Map } from 'mapbox-gl';

interface LocationData {
  lng: number;
  lat: number;
  address?: string;
}

interface UseMapInteractionProps {
  map: Map | null;
  isMobile: boolean;
  enabled?: boolean;
}

interface UseMapInteractionReturn {
  contextMenuOpen: boolean;
  contextMenuPosition: { x: number; y: number } | null;
  contextMenuLocation: LocationData | null;
  contextMenuLoading: boolean;
  closeContextMenu: () => void;
}

// Simple in-memory cache for geocoding results
const geocodeCache = new Map<string, string>();
const CACHE_MAX_SIZE = 50;

// LRU cache implementation
function getCacheKey(lng: number, lat: number): string {
  // Round to 4 decimal places (~11m precision) for cache key
  return `${lng.toFixed(4)},${lat.toFixed(4)}`;
}

function addToCache(key: string, value: string) {
  if (geocodeCache.size >= CACHE_MAX_SIZE) {
    // Remove oldest entry (first in Map)
    const firstKey = geocodeCache.keys().next().value;
    geocodeCache.delete(firstKey);
  }
  geocodeCache.set(key, value);
}

/**
 * Hook for handling map interactions (right-click, long-press)
 * and managing context menu state.
 */
export function useMapInteraction({
  map,
  isMobile,
  enabled = true,
}: UseMapInteractionProps): UseMapInteractionReturn {
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [contextMenuLocation, setContextMenuLocation] = useState<LocationData | null>(null);
  const [contextMenuLoading, setContextMenuLoading] = useState(false);

  // Long-press detection for mobile
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const closeContextMenu = useCallback(() => {
    setContextMenuOpen(false);
    setContextMenuPosition(null);
    setContextMenuLocation(null);
    setContextMenuLoading(false);
  }, []);

  // Reverse geocode coordinates to address
  const reverseGeocode = useCallback(async (lng: number, lat: number): Promise<string | undefined> => {
    const cacheKey = getCacheKey(lng, lat);
    
    // Check cache first
    if (geocodeCache.has(cacheKey)) {
      return geocodeCache.get(cacheKey);
    }

    try {
      const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      if (!token) {
        console.warn('[useMapInteraction] Mapbox token not configured');
        return undefined;
      }

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&limit=1`
      );

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const placeName = feature.place_name;
        
        // Format address nicely
        const parts = placeName.split(', ');
        const formatted = parts.join('\n');
        
        addToCache(cacheKey, formatted);
        return formatted;
      }
    } catch (error) {
      console.error('[useMapInteraction] Geocoding error:', error);
    }

    return undefined;
  }, []);

  // Open context menu at specific location
  const openContextMenu = useCallback(
    async (lng: number, lat: number, x: number, y: number) => {
      setContextMenuPosition({ x, y });
      setContextMenuLocation({ lng, lat });
      setContextMenuOpen(true);
      setContextMenuLoading(true);

      // Fetch address in background
      const address = await reverseGeocode(lng, lat);
      
      // Update location with address (if still open)
      setContextMenuLocation((prev) => 
        prev ? { ...prev, address } : null
      );
      setContextMenuLoading(false);
    },
    [reverseGeocode]
  );

  // Desktop: right-click handler
  useEffect(() => {
    if (!map || !enabled || isMobile) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault(); // Prevent default browser context menu

      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      
      // Get map coordinates
      const point = map.unproject([
        e.clientX - rect.left,
        e.clientY - rect.top,
      ]);

      openContextMenu(
        point.lng,
        point.lat,
        e.clientX,
        e.clientY
      );
    };

    const canvas = map.getCanvas();
    canvas.addEventListener('contextmenu', handleContextMenu);

    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [map, enabled, isMobile, openContextMenu]);

  // Mobile: long-press handler
  useEffect(() => {
    if (!map || !enabled || !isMobile) return;

    const LONG_PRESS_DURATION = 500; // ms
    const MOVE_THRESHOLD = 10; // px

    const handleTouchStart = (e: TouchEvent) => {
      // Only handle single touch
      if (e.touches.length !== 1) {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        return;
      }

      const touch = e.touches[0];
      touchStartPosRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      // Start timer for long-press
      longPressTimerRef.current = setTimeout(() => {
        const canvas = map.getCanvas();
        const rect = canvas.getBoundingClientRect();

        // Get map coordinates
        const point = map.unproject([
          touch.clientX - rect.left,
          touch.clientY - rect.top,
        ]);

        // Trigger haptic feedback (if available)
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }

        openContextMenu(
          point.lng,
          point.lat,
          touch.clientX,
          touch.clientY
        );

        longPressTimerRef.current = null;
      }, LONG_PRESS_DURATION);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!longPressTimerRef.current || !touchStartPosRef.current) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);

      // Cancel long-press if finger moved too much
      if (deltaX > MOVE_THRESHOLD || deltaY > MOVE_THRESHOLD) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const handleTouchEnd = () => {
      // Cancel long-press timer if touch ended early
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchStartPosRef.current = null;
    };

    const canvas = map.getCanvas();
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [map, enabled, isMobile, openContextMenu]);

  return {
    contextMenuOpen,
    contextMenuPosition,
    contextMenuLocation,
    contextMenuLoading,
    closeContextMenu,
  };
}
